import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArticleGenerationRow } from '../../../types/articleGeneration';
import { useNotification } from '../../../contexts/NotificationContext';
import { parseMarkdownBlocks } from './markdownBlocks';
import { exportFilename, exportJson, exportMarkdown, exportTitle } from './exportModel';
import { blocksToText } from './textExport';
import { downloadBlob } from './download';

export const EXPORT_FORMATS = ['docx', 'pdf', 'txt', 'md', 'json'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

interface Props {
  run: ArticleGenerationRow;
  disabled?: boolean;
}

/** Build the file for one format; the heavy libraries load only when asked for. */
export async function buildExport(run: ArticleGenerationRow, format: ExportFormat): Promise<Blob> {
  const markdown = exportMarkdown(run);
  switch (format) {
    case 'md':
      return new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    case 'txt':
      return new Blob([blocksToText(parseMarkdownBlocks(markdown))], { type: 'text/plain;charset=utf-8' });
    case 'json':
      return new Blob([JSON.stringify(exportJson(run), null, 2)], { type: 'application/json;charset=utf-8' });
    case 'docx': {
      const { buildDocx } = await import('./docxExport');
      return buildDocx(exportTitle(run), parseMarkdownBlocks(markdown));
    }
    case 'pdf': {
      const { buildPdf } = await import('./pdfExport');
      return buildPdf(exportTitle(run), parseMarkdownBlocks(markdown));
    }
  }
}

const PILL =
  'px-2.5 py-1 text-[11px] border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

/** A row of minimal buttons above the generated artifact: downloads and copies. */
const ExportButtons: React.FC<Props> = ({ run, disabled = false }) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [copied, setCopied] = useState<'markdown' | 'formatted' | null>(null);
  // Hidden render of the artifact: its HTML is what "copy formatted" puts on the clipboard.
  const renderedRef = useRef<HTMLDivElement>(null);
  const markdown = useMemo(() => exportMarkdown(run), [run]);

  const flashCopied = (kind: 'markdown' | 'formatted') => {
    setCopied(kind);
    setTimeout(() => setCopied(null), 2000);
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      flashCopied('markdown');
    } catch {
      showNotification(t('article.copy_failed'), 'error');
    }
  };

  const copyFormatted = async () => {
    try {
      const html = renderedRef.current?.innerHTML ?? '';
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([markdown], { type: 'text/plain' }),
        }),
      ]);
      flashCopied('formatted');
    } catch {
      showNotification(t('article.copy_failed'), 'error');
    }
  };

  const download = async (format: ExportFormat) => {
    setBusy(format);
    try {
      downloadBlob(await buildExport(run, format), exportFilename(run, format));
    } catch (error) {
      showNotification(
        t('article.export_failed', {
          format: format.toUpperCase(),
          message: error instanceof Error ? error.message : String(error),
        }),
        'error'
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label={t('article.export_group')}>
      <span className="text-xs text-gray-500 mr-1">{t('article.export_label')}</span>
      {EXPORT_FORMATS.map((format) => (
        <button
          key={format}
          type="button"
          onClick={() => download(format)}
          disabled={disabled || busy !== null}
          aria-busy={busy === format}
          title={t('article.export_as', { format: format.toUpperCase() })}
          className={`${PILL} font-mono uppercase tracking-wide`}
        >
          {busy === format ? '…' : format}
        </button>
      ))}
      <span aria-hidden="true" className="mx-1 h-4 w-px bg-gray-300" />
      <button type="button" onClick={copyMarkdown} disabled={disabled} className={PILL}>
        {copied === 'markdown' ? t('article.copied') : t('article.copy_markdown')}
      </button>
      <button type="button" onClick={copyFormatted} disabled={disabled} className={PILL}>
        {copied === 'formatted' ? t('article.copied') : t('article.copy_formatted')}
      </button>
      <div ref={renderedRef} className="hidden" data-testid="export-rendered">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
      </div>
    </div>
  );
};

export default ExportButtons;
